import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MessageCircle, ArrowDown, ShieldCheck, Truck, Layers, QrCode } from "lucide-react";
import { waLink, WA_DEFAULT_MSG, scrollToId } from "../lib/site";
import { useContent } from "../context/ContentContext";
import { imgUrl } from "../lib/api";

const DEFAULT_HERO_IMG = "/images/hero-banner.jpg";
const STAT_ICONS = [ShieldCheck, Truck, Layers, QrCode];

export default function Hero() {
  const content = useContent();
  const h = content.hero;
  const titleLines = [h.title1, h.title2, h.title3];
  const stats = content.stats || [];
  const heroImg = content.hero_image ? imgUrl(content.hero_image) : DEFAULT_HERO_IMG;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      id="inicio"
      ref={ref}
      className="spotlight grain relative flex min-h-screen flex-col justify-end overflow-hidden"
      data-testid="hero-section"
    >
      <motion.div style={{ y: imgY }} className="absolute inset-0">
        <img src={heroImg} alt="Portada Landerlan Dominicana" className="h-[120%] w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/55 to-black/85" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-16 pt-36 lg:px-8">
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.7 }}
          className="font-mono mb-6 text-xs uppercase tracking-[0.3em] text-brand-soft"
          data-testid="hero-overline"
        >
          {h.overline}
        </motion.p>

        <h1 className="font-display text-4xl font-black uppercase leading-[0.95] tracking-tight text-white sm:text-5xl lg:text-7xl" data-testid="hero-title">
          {titleLines.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{ delay: 0.25 + i * 0.16, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                className="block"
              >
                {i === 1 ? (
                  <span className="bg-gradient-to-r from-brand-soft via-[#D4AF37] to-brand bg-clip-text text-transparent">
                    {line}
                  </span>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.8 }}
          className="mt-6 max-w-xl text-sm leading-relaxed text-white/80 sm:text-base"
          data-testid="hero-subtitle"
        >
          {h.subtitle}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.8 }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <button
            onClick={() => scrollToId("#catalogo")}
            data-testid="hero-catalog-button"
            className="group flex items-center gap-2 rounded-full bg-brand px-7 py-3.5 text-sm font-bold text-white transition-all duration-300 hover:bg-brand-dark hover:shadow-[0_0_36px_rgba(184,145,47,0.45)]"
          >
            Explorar Catálogo
            <ArrowDown size={16} className="transition-transform duration-300 group-hover:translate-y-0.5" />
          </button>
          <a
            href={waLink(WA_DEFAULT_MSG, content.whatsapp_number)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-whatsapp-button"
            className="wa-pulse flex items-center gap-2 rounded-full border border-wa/60 bg-wa/15 px-7 py-3.5 text-sm font-bold text-wa transition-all duration-300 hover:bg-wa hover:text-black"
          >
            <MessageCircle size={16} strokeWidth={2.5} />
            Pedir por WhatsApp
          </a>
        </motion.div>

        <div className="mt-16 grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="hero-stats">
          {stats.map((s, i) => {
            const Icon = STAT_ICONS[i % STAT_ICONS.length];
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.2 + i * 0.12, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                className="glass-dark rounded-2xl border border-white/15 p-4"
                data-testid={`hero-stat-${i}`}
              >
                <Icon size={18} className="mb-2 text-brand-soft" />
                <p className="font-display text-xl font-extrabold text-white">{s.value}</p>
                <p className="mt-0.5 text-[11px] leading-snug text-white/60">{s.label}</p>
              </motion.div>
            );
          })}
        </div>
      </motion.div>
    </section>
  );
}
